import React, { useState, useContext, useRef, useEffect } from 'react';
import { Grid, TextField, Typography, Box, Alert } from '@mui/material';
import { StyledButton } from '../../components/ui/input/StyledButton';
import { useNavigate, useOutletContext } from 'react-router-dom';
import useInput from '../../hooks/useInput';
import { isNotEmpty } from '../../scripts/common/helper';
import { customFetch } from '../../scripts/common/customFetch';
import useSessionStorage from '../../hooks/useSessionStorage';
import { KioskContext } from '../../context/KioskProviderContext';
import * as Storage from '../../components/common/Storage';
import { TEST_AUTHENTICATED } from '../../components/assessment/TestStatus';
import { WINDOWS } from '../../scripts/Platform';
import { emptyClipBoardForWindowSecureBrowser } from '../../scripts/KioskModeChecker';
import parse from 'html-react-parser';

import { useTranslation } from 'react-i18next';

const Login = (props) => {
  const navigate = useNavigate();
  const { isSecure, platform } = useContext(KioskContext);
  const [setIsLoading] = useOutletContext();
  const { clearStorage, setStorageItem } = useSessionStorage();
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [loginError, setLoginError] = useState('');
  const loginIdRef = useRef();
  const { t } = useTranslation();

  const {
    value: idValue,
    isValid: idIsValid,
    hasError: idHasError,
    valueChangeHandler: idChangeHandler,
    reset: idReset,
  } = useInput(isNotEmpty);

  const {
    value: accessCodeValue,
    isValid: accessCodeIsValid,
    hasError: accessCodeHasError,
    valueChangeHandler: accessCodeChangeHandler,
    reset: accessCodeReset,
  } = useInput(isNotEmpty);

  const attemptDelays = {
    5: 0,
    6: 3,
    7: 3,
    8: 6,
    9: 6,
    10: 6,
    11: 6,
    12: 6,
    13: 10,
  };

  const clearLoginError = () => {
    if (idValue?.length > 0 || accessCodeValue?.length > 0) {
      setLoginError('');
    }
  };

  const delayLogin = () => {
    setLoginDisabled(true);
    let attempt = attemptCount >= 13 ? 13 : attemptCount + 1;
    const delays = attemptDelays[attempt];
    let backOfftime = delays * 1000;
    setTimeout(() => {
      setLoginDisabled(false);
      setLoginError('');
    }, backOfftime);
  };

  const logInUser = async () => {
    clearStorage();

    setIsLoading(true);
    const { data, errorObj } = await customFetch({
      url: `${window.env.REACT_APP_LOGIN_URL}?id=${idValue}&accessCode=${accessCodeValue}&secureStatus=${isSecure}`,
      headers: {
        "X-Nmc-Pwa-Version": `UI build ${process.env.REACT_APP_BUILD_VERSION} (v${process.env.REACT_APP_VERSION})`
      }
    });
    idReset();
    accessCodeReset();
    let errorMsg = '';
    if (errorObj) {
      setAttemptCount((prevState) => prevState + 1);
      if (errorObj.status === 403) {
        if (errorObj.error === 'CREDENTIALS_NOT_FOUND') {
          errorMsg += t('pages.login.invalidLogin');
        } else if (errorObj.error === 'SECURED_CLIENT_REQUIRED') {
          if (platform === WINDOWS && typeof window.SecureBrowser === 'undefined') {
            errorMsg += t('pages.login.launchingSecureTest');
            window.location.href = `ms-edu-secureassessment:${window.location.href}#enforcelockdown`;
          } else {
            errorMsg += t('pages.login.mustBeInKiosk');
          }
        } else if (errorObj.error === 'NON_SECURED_CLIENT_REQUIRED') {
          errorMsg += t('pages.login.cannotInKiosk');
        } else if (errorObj.error.startsWith('UNAUTHORIZED:')) {
          errorMsg += errorObj.error.replace('UNAUTHORIZED:', '');
        } else {
          errorMsg += t('pages.login.generalLoginError');
        }
      } else {
        errorMsg += t('pages.login.generalLoginError');
      }

      if (attemptCount > 4) {
        errorMsg = t('pages.login.tooManyAttempts');
        delayLogin();
      }
      setIsLoading(false);
      setLoginError(errorMsg);
      loginIdRef.current.focus();
    } else {
      setIsLoading(false);
      setStorageItem(Storage.STORAGE_AUTH_TOKEN, data.authToken);
      setStorageItem(Storage.STORAGE_TEST_STATUS, TEST_AUTHENTICATED);
      //Clear clipboard with platform is windows
      if (platform === WINDOWS) {
        emptyClipBoardForWindowSecureBrowser();
      }
      navigate('/assessment', {
        state: { ...data, accessCode: accessCodeValue },
      });
    }
  };

  const onEnterKeyDown = async (event) => {
    if (
      idIsValid &&
      accessCodeIsValid &&
      !loginDisabled &&
      event.key === 'Enter'
    ) {
      document.activeElement.blur();
      await logInUser();
    }
  };

  useEffect(() => {
    if(!loginDisabled) {
      loginIdRef.current.focus();
    }
  }, [loginDisabled]);

  return (
    <Box sx={{ alignItems: 'center', justifyContent: 'center' }}>
      <Grid
        container
        spacing={5}
        alignContent="center"
        justifyContent="center"
        alignItems="center"
        direction="column"
      >
        <Grid container item direction="column">
          <Grid item>
            <Typography variant="h3" alignContent="left">
              {t('pages.landing.welcomeTitle')} {(document.htmlTitle && parse(document.htmlTitle)) || document.title}!
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h6">
              {t('pages.login.promptCredentials')}
            </Typography>
          </Grid>
        </Grid>
        <Grid container item direction="row" spacing={5}>
          <Grid item>
            <TextField
              required
              error={idHasError}
              label={t('pages.login.idLabel')}
              helperText={t('pages.login.idHelper')}
              variant="outlined"
              sx={{ width: 400 }}
              InputLabelProps={{
                shrink: true,
              }}
              inputRef={loginIdRef}
              id="loginId"
              inputProps={{ 'data-testid': 'id-input', tabIndex: 1 }}
              value={idValue}
              onChange={(event, newValue, reason) => {
                if (loginError) {
                  clearLoginError();
                }
                idChangeHandler(event, newValue, reason);
              }}
              onBlur={(event) => {
                const newValue = event?.target?.value?.trim();
                idChangeHandler(event, newValue, 'trim');
              }}
              onKeyDown={onEnterKeyDown}
              disabled={loginDisabled}
              autoFocus
            />
          </Grid>
          <Grid item>
            <TextField
              required
              error={accessCodeHasError}
              label={t('pages.login.accessCodeLabel')}
              helperText={t('pages.login.accessCodeHelper')}
              variant="outlined"
              sx={{ width: 400 }}
              InputLabelProps={{
                shrink: true,
              }}
              id="accessCode"
              inputProps={{ 'data-testid': 'access-code-input', tabIndex: 2 }}
              value={accessCodeValue}
              onChange={(event, newValue, reason) => {
                if (loginError) {
                  clearLoginError();
                }
                accessCodeChangeHandler(event, newValue, reason);
              }}
              onBlur={(event) => {
                const newValue = event?.target?.value?.trim();
                accessCodeChangeHandler(event, newValue, 'trim');
              }}
              onKeyDown={onEnterKeyDown}
              disabled={loginDisabled}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            {loginError !== '' && <Alert severity="error">{loginError}</Alert>}
          </Grid>
          <Grid item>
            <StyledButton
              disabled={!idIsValid || !accessCodeIsValid || loginDisabled}
              onClick={logInUser}
              size="medium"
              tabIndex={3}
            >
              {t('common.login')}
            </StyledButton>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Login;
